'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ShieldAlert, Lock, ArrowLeft, MessageSquare, Shield, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';

interface AccessRestrictedProps {
  role: string;
  featureName: string;
  resource?: string;
  description?: string;
}

export function AccessRestricted({
  role,
  featureName,
  resource,
  description,
}: AccessRestrictedProps) {
  const router = useRouter();

  const roleFormatted = role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <div className="flex-1 h-full flex items-center justify-center p-4 sm:p-6 bg-muted/30 overflow-y-auto">
      <Card className="max-w-lg w-full shadow-xl border-border/80 bg-card/95 backdrop-blur-sm overflow-hidden animate-in fade-in-50 zoom-in-95 duration-200">
        <div className="h-2 w-full bg-gradient-to-r from-amber-500 via-rose-500 to-primary" />
        
        <CardHeader className="text-center pb-3 pt-8">
          <div className="relative mx-auto mb-4">
            <div className="size-20 rounded-3xl bg-rose-500/10 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center border border-rose-500/20 shadow-inner">
              <ShieldAlert className="size-10 stroke-[1.75]" />
            </div>
            <div className="absolute -bottom-1 -right-1 size-7 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-md border-2 border-card">
              <Lock className="size-3.5" />
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs px-2.5 py-0.5 font-semibold capitalize border-border/80 bg-muted/60 text-muted-foreground flex items-center gap-1.5">
              <Shield className="size-3 text-primary" />
              Role: {roleFormatted}
            </Badge>
            <Badge variant="outline" className="text-xs px-2 py-0.5 font-medium border-rose-500/30 text-rose-600 dark:text-rose-400 bg-rose-500/10">
              Restricted Access
            </Badge>
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Access Restricted
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            {description || (
              <>
                You do not have permission to access <strong className="text-foreground font-semibold">{featureName}</strong> under your current <strong className="text-foreground font-semibold">{roleFormatted}</strong> role.
              </>
            )}
          </p>
        </CardHeader>

        <CardContent className="px-6 py-4 space-y-4">
          <div className="rounded-xl border border-border/60 bg-muted/40 p-4 space-y-3 text-xs">
            <div className="flex justify-between items-center pb-2 border-b border-border/50">
              <span className="text-muted-foreground">Requested Page</span>
              <span className="font-semibold text-foreground">{featureName}</span>
            </div>
            <div className="flex justify-between items-center pb-2 border-b border-border/50">
              <span className="text-muted-foreground">Your Team Role</span>
              <span className="font-medium text-foreground capitalize">{roleFormatted}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Permission Status</span>
              <span className="text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1">
                <Lock className="size-3" /> Not Granted
              </span>
            </div>
          </div>

          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-primary/5 border border-primary/10 text-xs text-muted-foreground">
            <HelpCircle className="size-4 text-primary shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              To request access to <strong className="text-foreground">{featureName}</strong>, please contact your <strong className="text-foreground">Team Owner</strong> or Administrator to enable this permission in <span className="font-medium text-foreground">Settings → Team Members</span>.
            </p>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col sm:flex-row gap-2.5 px-6 pb-6 pt-2">
          <Button
            asChild
            className="w-full sm:flex-1 bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
          >
            <Link href="/dashboard" className="flex items-center justify-center gap-2">
              <MessageSquare className="size-4" />
              Go to Chats
            </Link>
          </Button>
          
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto flex items-center justify-center gap-2 border-border/80"
            onClick={() => {
              if (window.history.length > 1) {
                router.back();
              } else {
                router.push('/dashboard');
              }
            }}
          >
            <ArrowLeft className="size-4" />
            Go Back
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
